import React from 'react';
import { Badge, Group } from '@mantine/core';
import { REVIEW_DECISION_LABELS } from '@tinypro-audit/contracts';

import type { AuditReviewState, AuditStatus } from '@tinypro-audit/contracts';

/** Return the display label/color for the audit's primary status. */
function getStatusDisplay(status: AuditStatus): {
  color: string;
  label: string;
} {
  return {
    color:
      status === 'Ready' ? 'green' : status === 'Review' ? 'yellow' : 'red',
    label:
      status === 'Ready'
        ? 'Pre-site review passed'
        : status === 'Review'
          ? 'Pre-site follow-up'
          : status,
  };
}

/** Return true when the audit has already been manually resolved. */
function isResolved(reviewState: AuditReviewState): boolean {
  return reviewState.status === 'Resolved';
}

/** Reusable primary status badge shown throughout the audit UI. */
export function AuditStatusBadge({
  status,
}: {
  status: AuditStatus;
}): React.ReactElement {
  const indicator = getStatusDisplay(status);

  return (
    <Badge color={indicator.color} variant="light">
      {indicator.label}
    </Badge>
  );
}

/** Reusable answer badge used by audit-review question columns across the app. */
export function AuditReviewAnswerBadge({
  value,
}: {
  value: boolean | null;
}): React.ReactElement {
  if (value === true) {
    return (
      <Badge color="green" variant="light">
        Yes
      </Badge>
    );
  }

  if (value === false) {
    return (
      <Badge color="red" variant="light">
        No
      </Badge>
    );
  }

  return (
    <Badge color="gray" variant="light">
      Unanswered
    </Badge>
  );
}

/** Combined status cell that keeps resolution state inline with the main status. */
export function AuditStatusCell({
  status,
  reviewState,
}: {
  status: AuditStatus;
  reviewState: AuditReviewState;
}): React.ReactElement {
  return (
    <Group gap="xs">
      <AuditStatusBadge status={status} />
      {reviewState.decision && (
        <Badge
          color={isResolved(reviewState) ? 'blue' : 'gray'}
          variant="light"
        >
          {REVIEW_DECISION_LABELS[reviewState.decision.action]}
        </Badge>
      )}
    </Group>
  );
}
